import { Input, InputProps, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { GrispiInputProps } from '..';
import { usePublicPhoneCountryAndPlaceholder } from '../../../hooks/usePhoneCountryAndPlaceholder';
import {
    parseEmailFromSerializedValue,
    parsePhoneNumberFromSerializedValue
} from '../../../utils/forms/formHelper.parser';
import GrispiInputContainer from '../GrispiInputContainer';
type GrispiUntrustedEndUserInputProps = Omit<
    GrispiInputProps<'UNTRUSTED_END_USER'>,
    'type' | 'options' | 'isNewValueAllowed' | 'antdProps'
>;

const UserValueInputTypeMap = {
    fullName: 'fullName',
    email: 'email',
    phone: 'phone'
} as const;
type UserValueInputType = keyof typeof UserValueInputTypeMap;

const transformUserValue = (type: UserValueInputType, inputValue: string, formValue: string) => {
    if (typeof inputValue !== 'string' && typeof formValue !== 'string') return null;

    const safeInputValue = inputValue || null;

    let transformedUserValue: string;
    if (type === UserValueInputTypeMap.fullName)
        transformedUserValue = `${safeInputValue}:${parseEmailFromSerializedValue(
            formValue
        )}:${parsePhoneNumberFromSerializedValue(formValue)}`;
    if (type === UserValueInputTypeMap.email)
        transformedUserValue = `${parseFullNameFromSerializedValue(
            formValue
        )}:${safeInputValue}:${parsePhoneNumberFromSerializedValue(formValue)}`;
    if (type === UserValueInputTypeMap.phone)
        transformedUserValue = `${parseFullNameFromSerializedValue(formValue)}:${parseEmailFromSerializedValue(
            formValue
        )}:${safeInputValue}`;

    if (transformedUserValue === 'null:null:null') return null;
    return transformedUserValue;
};

const parseFullNameFromSerializedValue = (value: string) => {
    if (!value) return null;
    const possibleFullName = value.split(':')[0];
    if (!possibleFullName) return null;
    if (possibleFullName === 'null') return null;
    return possibleFullName;
};
const transformFormValue = (type: UserValueInputType, formValue: string) => {
    let transformedValue: string;
    if (type === UserValueInputTypeMap.fullName) transformedValue = parseFullNameFromSerializedValue(formValue);
    if (type === UserValueInputTypeMap.email) transformedValue = parseEmailFromSerializedValue(formValue);
    if (type === UserValueInputTypeMap.phone) transformedValue = parsePhoneNumberFromSerializedValue(formValue);
    if (transformedValue && typeof transformedValue === 'string') return transformedValue;
};

const GrispiUntrustedEndUserInput = ({
    containerProps,
    onChange,
    value,
    ...rest
}: GrispiUntrustedEndUserInputProps) => {
    const { t } = useTranslation('forms');

    const handleOnChange: (type: UserValueInputType) => InputProps['onChange'] =
        (type) =>
        ({ target: { value: inputValue } }) => {
            onChange(transformUserValue(type, inputValue, value));
        };

    const fullNameInputValue = transformFormValue(UserValueInputTypeMap.fullName, value);
    const emailInputValue = transformFormValue(UserValueInputTypeMap.email, value);
    const phoneNumberInputValue = transformFormValue(UserValueInputTypeMap.phone, value);

    const { phoneCountry, phonePlaceHolder } = usePublicPhoneCountryAndPlaceholder();

    return (
        <Space direction="vertical" className="full-width" size="small">
            <GrispiInputContainer
                {...containerProps}
                errorMessage={containerProps?.errorMessage?.split(',')?.at(0)}
                label={t('form.fullName.label') as unknown as string}
                description={t('form.fullName.label') as unknown as string}
            >
                <Input
                    allowClear
                    type="text"
                    onChange={handleOnChange(UserValueInputTypeMap.fullName)}
                    value={fullNameInputValue}
                    {...rest}
                />
            </GrispiInputContainer>
            <GrispiInputContainer
                {...containerProps}
                label={t('form.email.label') as unknown as string}
                description={t('form.email.label') as unknown as string}
                errorMessage={containerProps?.errorMessage?.split(',')?.at(1)}
            >
                <Input
                    allowClear
                    type="text"
                    onChange={handleOnChange(UserValueInputTypeMap.email)}
                    value={emailInputValue}
                    {...rest}
                />
            </GrispiInputContainer>
            <GrispiInputContainer
                {...containerProps}
                label={t('form.phone.label') as unknown as string}
                description={t('form.phone.label') as unknown as string}
                errorMessage={containerProps?.errorMessage?.split(',')?.at(2)}
            >
                <PhoneInput
                    onChange={(v) => {
                        if (v) onChange(transformUserValue(UserValueInputTypeMap.phone, v, value));
                        else onChange(transformUserValue(UserValueInputTypeMap.phone, '', value));
                    }}
                    value={phoneNumberInputValue}
                    numberInputProps={{
                        className: 'grispi-phone-input'
                    }}
                    defaultCountry={phoneCountry}
                    placeholder={phonePlaceHolder}
                    {...rest}
                />
            </GrispiInputContainer>
        </Space>
    );
};

export default GrispiUntrustedEndUserInput;
